import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { IconNames } from '../../../common/constants/icon-names';
import { AutoUnsubscribe } from '../../../common';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { cloneDeep, isEqual, isNil, omit, without } from 'lodash';
import { SelectedItem } from '../../../store/constants';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { IntrackFeatureModule } from '../../../intrack-feature.module';
import { SvgIconComponent } from '../../svg-icon/svg-icon.component';

@Component({
  selector: 'ui-chips-with-search-generic-and-checkboxes',
  imports: [CommonModule, IntrackFeatureModule, SvgIconComponent],
  templateUrl: './chips-with-search-generic-and-checkboxes.component.html',
  styleUrls: ['./chips-with-search-generic-and-checkboxes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe
export class ChipsWithSearchGenericAndCheckboxes<T extends { selected: boolean }> implements OnInit {
  @Input() label: string;
  @Input() isIconVisible = true;
  @Input() removable = true;
  @Input() optionNameKeys: string[];
  @Input() selectedItemsControl: UntypedFormControl;
  @Input() panelWidth?: string | number;
  @Input() set itemOptions(items: T[]) {
    if (items) {
      this.filteredItems = this.filterOptions(this.selectedItemsControl, items);
    }
  }
  @Input() selectedItems: T[];
  @Output() fetchItemsOptions: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  itemInputControl: FormControl<string> = new FormControl<string>('');
  filteredItems: SelectedItem<T>[] = [];
  subscriptions: Subscription[] = [];
  propertyName = 'selected';
  iconNames = IconNames;
  isNil = isNil;

  ngOnInit(): void {
    this.initInputControl(this.itemInputControl, this.fetchItemsOptions);
  }

  onOptionListClosed(input: HTMLInputElement): void {
    if (this.filteredItems?.length) {
      input.value = '';
      this.itemInputControl.setValue('');
    }
    this.resetOptions();
  }

  toggleSelection(itemOriginal: SelectedItem<T>): void {
    const item: SelectedItem<T> = cloneDeep(itemOriginal);
    item.selected = !item.selected;
    this.filteredItems = this.filteredItems.map((filteredItem: SelectedItem<T>) => {
      return isEqual(omit(filteredItem, this.propertyName), omit(itemOriginal, this.propertyName))
        ? item
        : filteredItem;
    });

    if (item.selected) {
      this.selectedItemsControl.setValue((this.selectedItemsControl.value ?? []).concat(item));
      this.selectedItemsControl.markAsDirty();
    } else {
      this.selectedItemsControl.setValue(
        (this.selectedItemsControl.value ?? []).filter(
          (value: SelectedItem<T>) => !isEqual(omit(value, this.propertyName), omit(item, this.propertyName)),
        ),
      );
      this.selectedItemsControl.markAsDirty();
    }
  }

  removeElement(selectedItemsControl: UntypedFormControl, item: SelectedItem<T>): void {
    selectedItemsControl.setValue(without(selectedItemsControl.value, item));
    selectedItemsControl.markAsDirty();

    this.filteredItems.forEach((filteredItem: SelectedItem<T>) => {
      if (isEqual(filteredItem, item)) {
        filteredItem.selected = !filteredItem.selected;
      }
    });
  }

  private filterOptions(selectedOptionsControl: UntypedFormControl, allOptions: T[]): T[] {
    const value: SelectedItem<T>[] = (selectedOptionsControl && selectedOptionsControl.value) || [];
    const selectedOptions: T[] = value.map((item: SelectedItem<T>) => {
      const tempItem: SelectedItem<T> = { ...item };
      delete tempItem.selected;
      return tempItem;
    });

    return allOptions.filter(
      (option: T) => !selectedOptions.some((selectedOption: T) => isEqual(option, selectedOption)),
    );
  }

  private initInputControl(control: FormControl<string>, emitter: EventEmitter<string>): void {
    this.subscriptions.push(
      control.valueChanges.pipe(debounceTime(500)).subscribe((value: string) => {
        if (value.length === 0) {
          this.resetOptions();
          this.autocomplete.closePanel();
        } else {
          emitter.emit(value);
        }
      }),
    );
  }

  private resetOptions(): void {
    this.filteredItems = [];
  }
}
